/**
 * Cookie-consent state + helpers for MakerX Multiplayer.
 *
 * A small, dependency-free implementation of the qrx-codes GDPR approach:
 * two categories (strictly-necessary, always on; analytics, opt-in), the
 * choice persisted in the `cc_cookie` cookie, and the banner shown only to
 * EU/EEA/UK visitors (see `isLikelyEU`).
 *
 * The analytics category gates Google Analytics (GA4). This file owns the
 * *decision* only — it stays dependency-free and never touches gtag directly.
 * `applyAnalyticsConsent` broadcasts changes via `ANALYTICS_CONSENT_EVENT`,
 * and the analytics module (`lib/analytics.ts`) listens and loads/holds GA
 * accordingly. Outside the EU there is no banner, so consent is implied (see
 * `analyticsConsentGranted`).
 */
import { isLikelyEU } from "./isLikelyEU.js";

/** Persisted consent decision. `analytics` is the only optional category. */
export interface ConsentChoice {
  analytics: boolean;
  /** epoch ms the choice was recorded — lets us expire/re-prompt later. */
  ts: number;
}

const COOKIE_NAME = "cc_cookie";
const ONE_YEAR_SECONDS = 60 * 60 * 24 * 365;

/** Custom DOM event used to re-open the preferences modal from anywhere
 *  (e.g. the footer "Cookie preferences" link) without prop-drilling. */
export const OPEN_PREFERENCES_EVENT = "cc:open-preferences";

/** Fired on the window whenever the analytics consent decision changes, so
 *  the analytics module can react (load GA on opt-in, stop on opt-out)
 *  without a circular import. */
export const ANALYTICS_CONSENT_EVENT = "cc:analytics-consent";

/**
 * The effective analytics-consent decision for this visitor:
 *  - Outside the EU the banner never shows, so consent is implied (`true`).
 *  - Inside the EU it's the stored opt-in choice (`false` until granted).
 * This is the single gate the analytics module reads before loading GA.
 */
export function analyticsConsentGranted(): boolean {
  if (!cookieConsentActive()) return true;
  return readConsent()?.analytics === true;
}

/** True when the consent system applies to this visitor (likely EU/EEA/UK).
 *  Drives both whether the banner is shown and whether the footer offers a
 *  "Cookie preferences" link. */
export function cookieConsentActive(): boolean {
  return isLikelyEU();
}

/** Read the stored consent choice, or `null` if none has been recorded. */
export function readConsent(): ConsentChoice | null {
  if (typeof document === "undefined") return null;
  const match = document.cookie
    .split("; ")
    .find((row) => row.startsWith(`${COOKIE_NAME}=`));
  if (!match) return null;
  try {
    const parsed = JSON.parse(
      decodeURIComponent(match.slice(COOKIE_NAME.length + 1)),
    ) as Partial<ConsentChoice>;
    if (typeof parsed.analytics !== "boolean") return null;
    return { analytics: parsed.analytics, ts: parsed.ts ?? 0 };
  } catch {
    return null;
  }
}

/** Persist the consent choice (1-year cookie) and apply it. */
export function writeConsent(analytics: boolean): void {
  const choice: ConsentChoice = { analytics, ts: Date.now() };
  const value = encodeURIComponent(JSON.stringify(choice));
  // The synchronous document.cookie API is the right fit for a single tiny
  // first-party consent cookie; the async Cookie Store API isn't universally
  // supported.
  // biome-ignore lint/suspicious/noDocumentCookie: simple first-party consent cookie
  document.cookie = `${COOKIE_NAME}=${value}; path=/; max-age=${ONE_YEAR_SECONDS}; SameSite=Lax`;
  applyAnalyticsConsent(analytics);
}

let appliedAnalytics: boolean | undefined;

/**
 * Apply the analytics consent decision by broadcasting it. The analytics
 * module (`lib/analytics.ts`) listens for `ANALYTICS_CONSENT_EVENT` and loads
 * GA on opt-in / stops sending pageviews on opt-out. Kept indirection (an
 * event, not a direct call) so this file pulls in no analytics/gtag code.
 */
export function applyAnalyticsConsent(granted: boolean): void {
  if (appliedAnalytics === granted) return;
  appliedAnalytics = granted;
  if (typeof window !== "undefined") {
    window.dispatchEvent(
      new CustomEvent(ANALYTICS_CONSENT_EVENT, { detail: granted }),
    );
  }
}

/** Re-open the cookie preferences modal (e.g. from a footer link). No-op if
 *  the banner component isn't mounted (non-EU visitors). */
export function showCookiePreferences(): void {
  if (typeof window === "undefined") return;
  window.dispatchEvent(new Event(OPEN_PREFERENCES_EVENT));
}
